import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Chip, 
  Stack,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import { 
  Download, 
  Visibility, 
  Search,
  Code,
  Business,
  Shield,
  ViewModule,
  ViewList
} from '@mui/icons-material';
import { useApi } from '../services/api';

interface Template {
  id: string;
  name: string;
  vendor: string;
  switch_type: string;
  description?: string;
  is_standard?: boolean;
  version: string;
  template_content?: string;
  created_at: string;
  updated_at: string;
}

const TemplateLibrary: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const { getAuthenticatedApi } = useApi();

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      setError(null);
      try {
        const api = await getAuthenticatedApi();
        const response = await api.listTemplates();
        
        // Handle both array and object responses
        const templateArray = Array.isArray(response) ? response : (response?.templates || []);
        setTemplates(templateArray);
      } catch (error: any) {
        setError(error.message || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTemplates();
  }, []); // Remove getAuthenticatedApi from dependencies - it doesn't change

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const api = await getAuthenticatedApi();
      const response = await api.listTemplates();
      
      // Handle both array and object responses
      const templateArray = Array.isArray(response) ? response : (response?.templates || []);
      setTemplates(templateArray);
    } catch (error: any) {
      setError(error.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (template: Template) => {
    setSelectedTemplate(template);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedTemplate(null);
  };

  const getVendorColor = (vendor: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' } = {
      cisco: 'primary',
      extreme: 'secondary',
      arista: 'success',
      juniper: 'warning',
    };
    return colors[vendor.toLowerCase()] || 'default';
  };

  const getTemplateIcon = (switchType: string) => {
    if (switchType.includes('edge')) return <Code />;
    if (switchType.includes('core')) return <Shield />;
    return <Business />;
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.switch_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={loadTemplates} variant="contained">
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Template Library</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Browse available configuration templates for various switch vendors and types
      </Typography>
      
      <Box display="flex" gap={2} mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search templates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => newMode && setViewMode(newMode)}
          aria-label="view mode"
        >
          <ToggleButton value="list" aria-label="list view">
            <ViewList />
          </ToggleButton>
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModule />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {filteredTemplates.length === 0 ? (
        <Alert severity="info">
          No templates found. {searchTerm && 'Try adjusting your search.'}
        </Alert>
      ) : viewMode === 'list' ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Switch Type</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getTemplateIcon(template.switch_type)}
                      <Typography sx={{ ml: 1 }}>{template.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={template.vendor} 
                      size="small" 
                      color={getVendorColor(template.vendor)}
                    />
                  </TableCell>
                  <TableCell>{template.switch_type}</TableCell>
                  <TableCell>{template.version}</TableCell>
                  <TableCell>
                    {template.is_standard && (
                      <Chip label="System" size="small" color="info" />
                    )}
                    {!template.is_standard && (
                      <Chip label="Custom" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{template.description || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      onClick={() => handlePreview(template)}
                      title="Preview"
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      disabled
                      title="Export functionality coming soon"
                    >
                      <Download />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Grid container spacing={3}>
          {filteredTemplates.map((template) => (
            <Grid size={{ xs: 12, md: 4 }} key={template.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    {getTemplateIcon(template.switch_type)}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {template.name}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
                    <Chip 
                      label={template.vendor} 
                      size="small" 
                      color={getVendorColor(template.vendor)}
                    />
                    <Chip 
                      label={template.switch_type} 
                      size="small" 
                      variant="outlined" 
                    />
                    {template.is_standard && (
                      <Chip 
                        label="System" 
                        size="small" 
                        color="info"
                      />
                    )}
                  </Stack>
                  {template.description && (
                    <Typography variant="body2" color="text.secondary">
                      {template.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Version: {template.version}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<Visibility />}
                    onClick={() => handlePreview(template)}
                  >
                    Preview
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<Download />}
                    disabled
                    title="Export functionality coming soon"
                  >
                    Export
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedTemplate?.name} - Template Preview
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1}>
              <Chip label={selectedTemplate?.vendor || ''} color={getVendorColor(selectedTemplate?.vendor || '')} />
              <Chip label={selectedTemplate?.switch_type || ''} />
              <Chip label={`Version ${selectedTemplate?.version}`} variant="outlined" />
            </Stack>
          </Box>
          {selectedTemplate?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedTemplate.description}
            </Typography>
          )}
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: '60vh',
              fontSize: '0.875rem',
              fontFamily: 'monospace'
            }}
          >
            {selectedTemplate?.template_content || 'Template content not available'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateLibrary;